import { Image, Typography } from "@jewlr/storybook/core"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import {
  addChooseGemstoneStep,
  removeChooseGemstoneStep,
  updateDiamond,
} from "areas/product/store/actions"
import { OptionsSlider } from "areas/product/wizard-options/components"
import { jewlr_t } from "helpers/application"
import { useNavigation } from "helpers/use-navigation"

import { ChoiceOption } from "./components"

export const StoneDescription = styled(Typography).attrs((props) => ({
  fontSize: 15,
  mb: 3,
  ...props.theme.chooseStoneDescription,
  ...props,
}))``

export const StoneOptionTitle = styled(Typography).attrs((props) => ({
  fontSize: { _: 14, tablet: 16 },
  lineHeight: 1.1,
  ...props.theme.wizardChoiceTitle,
  ...props,
}))``

const ChoiceOptions = ({
  selectedStoneType,
  stoneTypeOptions,
  setChoice,
  focusElementRef,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <OptionsSlider
      aria-label="Stone Type Options"
      data-slider="true"
      role="radiogroup"
    >
      {stoneTypeOptions.slice(0, 3).map((stone, i) => {
        const stoneType = stone.is_diamond ? stone.value : "gemstones"
        const selected = selectedStoneType === stoneType
        const focused = selectedStoneType ? selected : i === 0

        return (
          <ChoiceOption
            aria-selected={selected}
            data-cy={`stone-type-${stoneType}`}
            flexBasis={{ _: "33.333%", desktop: "32%", tablet: "30%" }}
            key={`d_stone_type_${stoneType}`}
            onClick={() => setChoice(stone)}
            role="radio"
            selected={selected}
            tabIndex={focused ? 0 : -1}
            {...(focused && { ref: focusElementRef })}
            {...keyboardProps}
          >
            <Image
              alt={stone.title}
              src={`${sl.config.jewlr_asset_url}images/product/diamond/stones/${stoneType}.png`}
              srcSet={`${`${sl.config.jewlr_asset_url}images/product/diamond/stones/${stoneType}.png`} 1x, ${`${sl.config.jewlr_asset_url}images/product/diamond/stones/${stoneType}_2x.png`} 2x`}
              width="64px"
            />
            <StoneOptionTitle>
              {stone.is_diamond ? stone.title : "Gemstones"}
            </StoneOptionTitle>
          </ChoiceOption>
        )
      })}
    </OptionsSlider>
  )
}

ChoiceOptions.propTypes = {
  focusElementRef: PropTypes.object,
  selectedStoneType: PropTypes.string,
  setChoice: PropTypes.func,
  stoneTypeOptions: PropTypes.array,
}

const InternalChooseStoneTypes = ({
  selectedStoneType,
  stoneTypeOptions,
  selectedStoneTypeDescription,
  setChoice,
}) => {
  const focusElementRef = useRef()

  useEffect(() => {
    if (focusElementRef.current) {
      focusElementRef.current.focus()
    }
  }, [focusElementRef.current])

  if (!stoneTypeOptions) return null
  return (
    <>
      <FocusScope>
        <ChoiceOptions
          focusElementRef={focusElementRef}
          selectedStoneType={selectedStoneType}
          setChoice={setChoice}
          stoneTypeOptions={stoneTypeOptions}
        />
      </FocusScope>
      <StoneDescription>
        {["04DIAM", "04LABDIAM"].includes(selectedStoneType)
          ? selectedStoneTypeDescription
          : `Our precious gemstones are carefully sourced for their combination of quality and ${jewlr_t(
              "terms.color"
            )}, resulting in the most beautiful stones and best value. Your purchase includes a certificate of authenticity.`}
      </StoneDescription>
    </>
  )
}

const mapStateToProps = (state) => {
  const selectedStoneType = state.choices.d_stone_type.value
  const selectedStoneTypeDescription = state.diamondSelector[
    "d_stone_type"
  ]?.find((opt) => opt.value === selectedStoneType)?.description
  return {
    selectedStoneType,
    selectedStoneTypeDescription,
    stoneTypeOptions: state.diamondSelector.d_stone_type,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setChoice: (stone) => {
      if (stone.is_diamond) {
        dispatch(removeChooseGemstoneStep())
      } else {
        dispatch(addChooseGemstoneStep())
      }
      dispatch(updateDiamond("d_stone_type", stone))
    },
  }
}

InternalChooseStoneTypes.propTypes = {
  selectedStoneType: PropTypes.string,
  selectedStoneTypeDescription: PropTypes.string,
  setChoice: PropTypes.func,
  stoneTypeOptions: PropTypes.array,
}

const ChooseStoneTypes = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalChooseStoneTypes)

export default ChooseStoneTypes
